import React, { useEffect, useRef, useState } from 'react';
import { insightService, Insight, InsightAttachment, InsightComment, InsightLink, Sentiment } from '../services/insightService';
import { customerService, CustomerUser } from '../services/customerService';
import { organizationService, OrgMember } from '../services/organizationService';
import { themeService } from '../services/themeService';
import { projectStore } from '../lib/projectStore';
import { useAuth } from '../contexts/AuthContext';
import TaskDescriptionEditor from './TaskDescriptionEditor';
import '../styles/CustomerModal.css';
import '../styles/InsightModal.css';

interface InsightModalProps {
  insight?: Insight | null;
  onClose: () => void;
  onSaved: (insight: Insight) => void;
}

type TabKey = 'description' | 'history';

const SENTIMENT_OPTIONS: { value: Sentiment; label: string; color: string }[] = [
  { value: 'positive', label: 'Positive', color: '#10b981' },
  { value: 'neutral',  label: 'Neutral',  color: '#6366f1' },
  { value: 'negative', label: 'Negative', color: '#ef4444' },
];

const InsightModal: React.FC<InsightModalProps> = ({ insight, onClose, onSaved }) => {
  const { currentOrg, user } = useAuth();
  const isEdit = !!insight;

  const [title, setTitle]     = useState(insight?.title ?? '');
  const [details, setDetails] = useState(insight?.details ?? '');
  const [saving, setSaving]   = useState(false);
  const [activeTab, setActiveTab] = useState<TabKey>('description');
  const [showMenu, setShowMenu] = useState(false);

  // Sidebar field state
  const [assignee, setAssignee]       = useState<OrgMember | null>(null);
  const [assigneeQuery, setAssigneeQuery] = useState('');
  const [allMembers, setAllMembers]   = useState<OrgMember[]>([]);
  const [showAssigneeSearch, setShowAssigneeSearch] = useState(false);

  const [customerUserId, setCustomerUserId] = useState<string | null>(insight?.customer_user_id ?? null);
  const [customerUsers, setCustomerUsers]   = useState<CustomerUser[]>([]);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [customerQuery, setCustomerQuery]   = useState('');

  const [tags, setTags]     = useState<string[]>(insight?.tags ?? []);
  const [tagInput, setTagInput] = useState('');
  const [showTagInput, setShowTagInput] = useState(false);

  const [themes, setThemes] = useState<string[]>(insight?.themes ?? []);
  const [themeQuery, setThemeQuery] = useState('');
  const [showThemeSearch, setShowThemeSearch] = useState(false);
  const [availableThemes, setAvailableThemes] = useState<string[]>([]);

  const [sentiment, setSentiment] = useState<Sentiment | null>(insight?.sentiment ?? null);
  const [showSentimentDropdown, setShowSentimentDropdown] = useState(false);

  const [links] = useState<InsightLink[]>(insight?.links ?? []);
  const [linkPage, setLinkPage] = useState(0);

  // Processed state
  const [processed, setProcessed]   = useState<boolean>(insight?.processed ?? false);
  const [togglingProcessed, setTogglingProcessed] = useState(false);

  // Attachments
  const [attachments, setAttachments] = useState<InsightAttachment[]>([]);
  const [uploading, setUploading]     = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  // Comments
  const [comments, setComments] = useState<InsightComment[]>([]);
  const [commentInput, setCommentInput] = useState('');
  const [postingComment, setPostingComment] = useState(false);

  const fileInputRef     = useRef<HTMLInputElement>(null);
  const menuRef          = useRef<HTMLDivElement>(null);
  const assigneeInputRef = useRef<HTMLInputElement>(null);
  const customerRef      = useRef<HTMLDivElement>(null);
  const sentimentRef     = useRef<HTMLDivElement>(null);
  const themeInputRef    = useRef<HTMLInputElement>(null);

  // Load themes created in the Themes tab (org-scoped) for the picker
  useEffect(() => {
    themeService.getThemes(projectStore.getProjectId())
      .then(list => setAvailableThemes(list.map(t => t.name).filter(Boolean)))
      .catch(() => {});
  }, []);

  // Load org members for assignee
  useEffect(() => {
    if (!currentOrg) return;
    organizationService.getMembersWithProfiles(currentOrg.id)
      .then(members => {
        setAllMembers(members);
        if (insight?.assignee_id) {
          const match = members.find(m => m.user_id === insight.assignee_id);
          if (match) setAssignee(match);
        }
      })
      .catch(() => {});
  }, [currentOrg, insight]);

  // Load customer users for "Customer" picker
  useEffect(() => {
    customerService.getCustomerUsers(projectStore.getProjectId()).then(setCustomerUsers).catch(() => {});
  }, []);

  // Load existing attachments + comments when editing
  useEffect(() => {
    if (insight?.id) {
      insightService.getAttachments(insight.id).then(setAttachments).catch(() => {});
      insightService.getComments(insight.id).then(setComments).catch(() => {});
    }
  }, [insight?.id]);

  // Close menu on outside click
  useEffect(() => {
    if (!showMenu) return;
    const h = (e: MouseEvent) => { if (menuRef.current && !menuRef.current.contains(e.target as Node)) setShowMenu(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [showMenu]);

  // Close assignee search on outside click
  useEffect(() => {
    if (!showAssigneeSearch) return;
    const h = (e: MouseEvent) => {
      if (assigneeInputRef.current && !assigneeInputRef.current.parentElement?.contains(e.target as Node)) setShowAssigneeSearch(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [showAssigneeSearch]);

  // Close customer dropdown on outside click
  useEffect(() => {
    if (!showCustomerDropdown) return;
    const h = (e: MouseEvent) => { if (customerRef.current && !customerRef.current.contains(e.target as Node)) setShowCustomerDropdown(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [showCustomerDropdown]);

  // Close theme search on outside click
  useEffect(() => {
    if (!showThemeSearch) return;
    const h = (e: MouseEvent) => {
      if (themeInputRef.current && !themeInputRef.current.parentElement?.contains(e.target as Node)) setShowThemeSearch(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [showThemeSearch]);

  // Close sentiment dropdown on outside click
  useEffect(() => {
    if (!showSentimentDropdown) return;
    const h = (e: MouseEvent) => { if (sentimentRef.current && !sentimentRef.current.contains(e.target as Node)) setShowSentimentDropdown(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [showSentimentDropdown]);

  const assigneeMatches = allMembers.filter(m => {
    const q = assigneeQuery.trim().toLowerCase();
    if (!q) return true;
    return (m.full_name ?? '').toLowerCase().includes(q) || (m.email ?? '').toLowerCase().includes(q);
  }).slice(0, 8);

  const customerMatches = customerUsers.filter(c => {
    const q = customerQuery.trim().toLowerCase();
    return !q || c.name.toLowerCase().includes(q);
  }).slice(0, 10);

  const selectedCustomer = customerUsers.find(c => c.id === customerUserId) ?? null;

  const isImage = (type: string) => type.startsWith('image/');

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (!files.length || !currentOrg) return;
    e.target.value = '';
    if (!insight?.id) { alert('Please save the insight first, then add attachments.'); return; }
    setUploading(true);
    try {
      const uploaded = await Promise.all(files.map(f => insightService.uploadAttachment(insight.id, currentOrg.id, f)));
      setAttachments(prev => [...prev, ...uploaded]);
    } catch (err) {
      console.error('Upload failed:', err);
      alert(`Failed to upload: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveAttachment = async (att: InsightAttachment) => {
    if (!window.confirm(`Remove "${att.file_name}"?`)) return;
    try {
      await insightService.deleteAttachment(att);
      setAttachments(prev => prev.filter(a => a.id !== att.id));
    } catch {
      alert('Failed to remove attachment.');
    }
  };

  const addTag = () => {
    const val = tagInput.trim();
    if (val && !tags.includes(val)) setTags(prev => [...prev, val]);
    setTagInput('');
    setShowTagInput(false);
  };

  const removeTag = (tag: string) => setTags(prev => prev.filter(t => t !== tag));

  const toggleTheme = (name: string) => {
    setThemes(prev => prev.includes(name) ? prev.filter(t => t !== name) : [...prev, name]);
  };

  const removeTheme = (theme: string) => setThemes(prev => prev.filter(t => t !== theme));

  // Themes from the Themes tab that match the search and aren't already picked.
  const themeMatches = availableThemes.filter(name => {
    const q = themeQuery.trim().toLowerCase();
    return !q || name.toLowerCase().includes(q);
  }).slice(0, 8);

  const handleAddComment = async () => {
    if (!commentInput.trim() || !insight?.id) return;
    setPostingComment(true);
    try {
      const authorName = user?.user_metadata?.full_name || user?.email || 'Me';
      const comment = await insightService.addComment(insight.id, commentInput.trim(), authorName);
      setComments(prev => [...prev, comment]);
      setCommentInput('');
    } catch {
      alert('Failed to add comment.');
    } finally {
      setPostingComment(false);
    }
  };

  const handleSave = async () => {
    if (!title.trim()) { alert('Title is required'); return; }
    setSaving(true);
    try {
      const extra = {
        assignee_id: assignee?.user_id ?? null,
        customer_user_id: customerUserId,
        company_id: selectedCustomer?.company_id ?? null,
        sentiment,
        tags,
        themes,
        links,
        processed,
      };
      let saved: Insight;
      if (isEdit && insight) {
        saved = await insightService.updateInsight(insight.id, {
          title: title.trim(), details: details.trim(), ...extra,
        });
      } else {
        saved = await insightService.createInsight({
          title: title.trim(),
          details: details.trim(),
          project_id: projectStore.getProjectId(),
          ...extra,
        });
      }
      onSaved(saved);
    } catch (err) {
      console.error('Error saving insight:', err);
      alert('Failed to save insight. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleProcessed = async () => {
    // If no saved insight, we need to create one first (auto-save with current fields)
    if (!insight) {
      if (!title.trim()) { alert('Please enter a title first.'); return; }
      setTogglingProcessed(true);
      try {
        const extra = {
          assignee_id: assignee?.user_id ?? null,
          customer_user_id: customerUserId,
          company_id: selectedCustomer?.company_id ?? null,
          sentiment,
          tags,
          themes,
          links,
          processed: true,
        };
        const created = await insightService.createInsight({
          title: title.trim(),
          details: details.trim(),
          project_id: projectStore.getProjectId(),
          ...extra,
        });
        setProcessed(true);
        onSaved(created);
      } catch (err) {
        console.error('Error auto-creating insight:', err);
        alert(`Failed to create insight: ${err instanceof Error ? err.message : String(err)}`);
      } finally {
        setTogglingProcessed(false);
      }
      return;
    }

    // Existing insight - toggle the processed state
    setTogglingProcessed(true);
    try {
      const next = !processed;
      const updated = await insightService.updateInsight(insight.id, { processed: next });
      setProcessed(updated.processed);
      onSaved(updated);
    } catch (err) {
      console.error('Error toggling processed state:', err);
      alert(`Failed to update processed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setTogglingProcessed(false);
    }
  };

  return (
    <>
      <div className="cum-modal-backdrop" onClick={() => !saving && onClose()}>
        <div className="inline-edit-form premium-task-modal cum-company-modal" onClick={e => e.stopPropagation()}>

          {/* ── Header ── */}
          <div className="task-modal-header">
            <div className="header-titles-new">
              <h3>{isEdit ? 'Edit Insight' : 'Add Insight'}</h3>
              <p className="header-subtitle-new">Capture feedback, signals, and research</p>
            </div>
            <div className="cum-header-actions">
              <button
                type="button"
                className={`ism-processed-btn ${processed ? 'ism-processed-btn-active' : ''}`}
                onClick={handleToggleProcessed}
                disabled={togglingProcessed || saving}
                title={processed ? 'Mark as unprocessed' : 'Mark as processed'}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  {processed
                    ? <><polyline points="20 6 9 17 4 12"/></>
                    : <><circle cx="12" cy="12" r="9"/></>}
                </svg>
                {processed ? 'Mark Unprocessed' : 'Mark Processed'}
              </button>
              <div className="cum-menu-wrapper" ref={menuRef}>
                <button className="task-modal-close cum-dots-btn" onClick={() => setShowMenu(p => !p)} title="More options" disabled={saving}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <circle cx="12" cy="5" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="12" cy="19" r="1.5"/>
                  </svg>
                </button>
                {showMenu && (
                  <div className="cum-context-menu">
                    <button className="cum-menu-item" onClick={() => { navigator.clipboard.writeText(window.location.href).catch(() => {}); setShowMenu(false); }}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/></svg>
                      Copy link
                    </button>
                    <div className="cum-menu-divider" />
                    <button className="cum-menu-item" onClick={() => setShowMenu(false)}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
                      Duplicate
                    </button>
                    <button className="cum-menu-item cum-menu-item-muted" onClick={() => setShowMenu(false)}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg>
                      Archive
                    </button>
                    <button className="cum-menu-item cum-menu-item-danger" onClick={() => setShowMenu(false)}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg>
                      Delete insight
                    </button>
                  </div>
                )}
              </div>
              <button type="button" className="task-modal-close" onClick={onClose} disabled={saving}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
          </div>

          {/* ── Two-column content ── */}
          <div className="task-modal-content-layout">

            {/* LEFT COLUMN */}
            <div className="task-modal-main-column">

              <div className="form-group-new">
                <label className="section-label-new">Title <span style={{ color: '#dc2626' }}>*</span></label>
                <div className="title-input-wrapper-new">
                  <input
                    type="text"
                    className="task-title-input-new"
                    placeholder="Enter insight title"
                    value={title}
                    onChange={e => setTitle(e.target.value)}
                    autoFocus
                  />
                </div>
              </div>

              {/* Tabs: Description | History */}
              <div className="cum-tabs cum-tabs-inline">
                {(['description', 'history'] as TabKey[]).map(tab => (
                  <button key={tab} type="button" className={`cum-tab ${activeTab === tab ? 'active' : ''}`} onClick={() => setActiveTab(tab)}>
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                  </button>
                ))}
              </div>

              {activeTab === 'description' && (
                <>
                  <div className="form-group-new">
                    <TaskDescriptionEditor
                      key={`insight-${insight?.id ?? 'new'}`}
                      htmlValue={details}
                      onChange={setDetails}
                      placeholder="Describe the insight, feedback, or research finding…"
                    />
                  </div>

                  {/* Attachment previews */}
                  {attachments.length > 0 && (
                    <div className="cum-attachment-previews">
                      {attachments.map(att => (
                        <div key={att.id} className="cum-att-thumb">
                          {isImage(att.file_type) ? (
                            <img src={att.url} alt={att.file_name} className="cum-att-img" onClick={() => setLightboxUrl(att.url)} title={att.file_name} />
                          ) : (
                            <div className="cum-att-file" title={att.file_name}>
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="1.8" strokeLinecap="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                              <span className="cum-att-file-name">{att.file_name.length > 14 ? att.file_name.slice(0, 12) + '…' : att.file_name}</span>
                            </div>
                          )}
                          <button type="button" className="cum-att-remove" onClick={() => handleRemoveAttachment(att)} title="Remove">
                            <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
                              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                            </svg>
                          </button>
                        </div>
                      ))}
                      {uploading && (
                        <div className="cum-att-thumb cum-att-uploading">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.8" strokeLinecap="round" className="cum-att-spinner"><path d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" strokeDasharray="40" strokeDashoffset="20"/></svg>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Attachment button */}
                  <div className="form-group-new">
                    <input ref={fileInputRef} type="file" style={{ display: 'none' }} multiple accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt" onChange={handleFileChange} />
                    <div className="integration-buttons-grid-new">
                      <div className="integrations-grid-row-new">
                        <button type="button" className="integration-btn-new" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"/>
                          </svg>
                          {uploading ? 'Uploading…' : 'Attachment'}
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {activeTab === 'history' && (
                <div className="cum-insights-placeholder">
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                  <p>Activity history will appear here.</p>
                </div>
              )}

              {/* Comments section */}
              <div className="form-group-new comments-group-new">
                <div className="comment-tabs-bar">
                  <button type="button" className="comment-tab-btn active">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                    Comments
                    {comments.length > 0 && <span className="comment-tab-badge">{comments.length}</span>}
                  </button>
                </div>

                {comments.length > 0 && (
                  <div className="comments-feed-new">
                    {[...comments].reverse().map(comment => (
                      <div key={comment.id} className="comment-card-new">
                        <div className="comment-card-header-new">
                          <div className="comment-avatar-new">{comment.author_name?.charAt(0)?.toUpperCase() || 'U'}</div>
                          <div className="comment-meta-new">
                            <span className="comment-author-name-new">{comment.author_name}</span>
                            <span className="comment-dot-new">•</span>
                            <span className="comment-time-new">
                              {new Date(comment.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                        </div>
                        <div className="comment-body-new">{comment.text}</div>
                      </div>
                    ))}
                  </div>
                )}
                {comments.length === 0 && (
                  <div className="comment-empty-state">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                    <span>No comments yet</span>
                  </div>
                )}

                {/* Add Comment Input */}
                <div className="comment-input-row-new">
                  <div className="comment-avatar-new">{(user?.user_metadata?.full_name || user?.email || 'U')?.charAt(0)?.toUpperCase()}</div>
                  <div className="comment-input-box-wrapper-new">
                    <textarea
                      className="comment-textarea-new"
                      placeholder={insight?.id ? 'Add a comment…' : 'Save the insight first to add comments'}
                      value={commentInput}
                      onChange={e => setCommentInput(e.target.value)}
                      rows={1}
                      disabled={!insight?.id || postingComment}
                    />
                    <div className="comment-input-actions-new">
                      <button type="button" className="comment-send-btn-new" onClick={handleAddComment} disabled={!commentInput.trim() || !insight?.id || postingComment} title="Send Comment">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"></line><polygon points="22 2 15 22 11 13 2 9 22 2"></polygon></svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT COLUMN */}
            <div className="task-modal-sidebar-column">

              {/* Assignee */}
              <div className="form-group-sidebar-new">
                <label className="sidebar-label-new">Assignee</label>
                <div style={{ position: 'relative' }}>
                  {showAssigneeSearch ? (
                    <div className="cum-owner-search-box">
                      <input
                        ref={assigneeInputRef}
                        className="task-title-input-new cum-owner-input"
                        type="text"
                        placeholder="Search members…"
                        value={assigneeQuery}
                        onChange={e => setAssigneeQuery(e.target.value)}
                        autoFocus
                      />
                      <div className="cum-owner-dropdown">
                        <button type="button" className="cum-owner-option" onClick={() => { setAssignee(null); setAssigneeQuery(''); setShowAssigneeSearch(false); }}>
                          <span style={{ fontSize: 13, color: '#9ca3af', fontStyle: 'italic' }}>No assignee</span>
                        </button>
                        {assigneeMatches.length === 0 && assigneeQuery.trim() && <div className="cum-owner-empty">No members found</div>}
                        {assigneeMatches.map(m => (
                          <button key={m.id} type="button" className="cum-owner-option" onClick={() => { setAssignee(m); setAssigneeQuery(''); setShowAssigneeSearch(false); }}>
                            <span className="cum-owner-avatar">{(m.full_name || m.email || '?')[0].toUpperCase()}</span>
                            <div className="cum-owner-info">
                              <span className="cum-owner-name">{m.full_name || 'Unnamed'}</span>
                              <span className="cum-owner-email">{m.email}</span>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <button type="button" className="sidebar-select-trigger-new has-icon-new cum-owner-trigger" onClick={() => setShowAssigneeSearch(true)}>
                      {assignee ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span className="cum-owner-avatar cum-owner-avatar-sm">{(assignee.full_name || assignee.email || '?')[0].toUpperCase()}</span>
                          <span>{assignee.full_name || assignee.email}</span>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span className="cum-owner-circle" />
                          <span style={{ color: '#94a3b8' }}>Not assigned</span>
                        </div>
                      )}
                      <svg className="sidebar-select-chevron-new" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9"/></svg>
                    </button>
                  )}
                </div>
              </div>

              {/* Customer */}
              <div className="form-group-sidebar-new">
                <label className="sidebar-label-new">Customer</label>
                <div style={{ position: 'relative' }} ref={customerRef}>
                  {selectedCustomer ? (
                    <div className="ism-customer-card" onClick={() => { setShowCustomerDropdown(p => !p); setCustomerQuery(''); }}>
                      <span className="ism-customer-avatar">{selectedCustomer.name.slice(0, 2).toUpperCase()}</span>
                      <div className="ism-customer-info">
                        <span className="ism-customer-name">{selectedCustomer.name}</span>
                        {selectedCustomer.companies?.name && <span className="ism-customer-company">{selectedCustomer.companies.name}</span>}
                      </div>
                    </div>
                  ) : (
                    <button type="button" className="sidebar-select-trigger-new has-icon-new cum-owner-trigger" onClick={() => { setShowCustomerDropdown(p => !p); setCustomerQuery(''); }}>
                      <span style={{ color: '#94a3b8' }}>Not assigned</span>
                      <svg className="sidebar-select-chevron-new" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9"/></svg>
                    </button>
                  )}
                  {showCustomerDropdown && (
                    <div className="cum-owner-dropdown">
                      <div style={{ padding: '6px 8px 4px' }}>
                        <input
                          className="task-title-input-new cum-owner-input"
                          type="text"
                          placeholder="Search users…"
                          value={customerQuery}
                          onChange={e => setCustomerQuery(e.target.value)}
                          autoFocus
                          style={{ fontSize: 13, fontWeight: 400 }}
                        />
                      </div>
                      <button type="button" className="cum-owner-option" onClick={() => { setCustomerUserId(null); setShowCustomerDropdown(false); }}>
                        <span style={{ fontSize: 13, color: '#9ca3af', fontStyle: 'italic' }}>No customer</span>
                      </button>
                      {customerMatches.map(c => (
                        <button key={c.id} type="button" className="cum-owner-option" onClick={() => { setCustomerUserId(c.id); setShowCustomerDropdown(false); }}>
                          <span className="cum-owner-avatar cum-owner-avatar-sm">{c.name.slice(0, 2).toUpperCase()}</span>
                          <div className="cum-owner-info">
                            <span className="cum-owner-name">{c.name}</span>
                            {c.companies?.name && <span className="cum-owner-email">{c.companies.name}</span>}
                          </div>
                        </button>
                      ))}
                      {customerMatches.length === 0 && <div className="cum-owner-empty">No users found</div>}
                    </div>
                  )}
                  {selectedCustomer && (
                    <button type="button" className="ism-add-link-btn" onClick={() => { setShowCustomerDropdown(true); setCustomerQuery(''); }}>
                      + add user
                    </button>
                  )}
                </div>
              </div>

              {/* Tags */}
              <div className="form-group-sidebar-new">
                <label className="sidebar-label-new">Tags</label>
                <div className="ism-chip-row">
                  {tags.map(tag => (
                    <span key={tag} className="ism-chip">
                      {tag}
                      <button type="button" className="ism-chip-remove" onClick={() => removeTag(tag)}>
                        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                      </button>
                    </span>
                  ))}
                  {showTagInput ? (
                    <input
                      className="ism-chip-input"
                      autoFocus
                      value={tagInput}
                      onChange={e => setTagInput(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } if (e.key === 'Escape') { setTagInput(''); setShowTagInput(false); } }}
                      onBlur={addTag}
                      placeholder="Tag name"
                    />
                  ) : (
                    <button type="button" className="ism-add-chip-btn" onClick={() => setShowTagInput(true)}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                      Tag
                    </button>
                  )}
                </div>
              </div>

              {/* Themes — searchable picker (Assignee-style), sourced from the Themes tab */}
              <div className="form-group-sidebar-new">
                <label className="sidebar-label-new">Themes</label>
                <div style={{ position: 'relative' }}>
                  {showThemeSearch ? (
                    <div className="cum-owner-search-box">
                      <input
                        ref={themeInputRef}
                        className="task-title-input-new cum-owner-input"
                        type="text"
                        placeholder="Search themes…"
                        value={themeQuery}
                        onChange={e => setThemeQuery(e.target.value)}
                        autoFocus
                      />
                      <div className="cum-owner-dropdown">
                        {availableThemes.length === 0 && (
                          <div className="cum-owner-empty">No themes yet — add them in the Themes tab</div>
                        )}
                        {availableThemes.length > 0 && themeMatches.length === 0 && themeQuery.trim() && (
                          <div className="cum-owner-empty">No themes found</div>
                        )}
                        {themeMatches.map(name => {
                          const selected = themes.includes(name);
                          return (
                            <button key={name} type="button" className="cum-owner-option" onClick={() => toggleTheme(name)}>
                              <span className={`ism-theme-check ${selected ? 'ism-theme-check-on' : ''}`}>
                                {selected && (
                                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                                )}
                              </span>
                              <span style={{ fontSize: 13, color: '#111827' }}>{name}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <button type="button" className="sidebar-select-trigger-new has-icon-new cum-owner-trigger" onClick={() => { setShowThemeSearch(true); setThemeQuery(''); }}>
                      {themes.length > 0 ? (
                        <div className="ism-theme-trigger-chips">
                          {themes.map(theme => (
                            <span key={theme} className="ism-pill ism-pill-theme" onClick={e => e.stopPropagation()}>
                              {theme}
                              <button type="button" className="ism-chip-remove" onClick={e => { e.stopPropagation(); removeTheme(theme); }}>
                                <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                              </button>
                            </span>
                          ))}
                        </div>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span className="cum-owner-circle" />
                          <span style={{ color: '#94a3b8' }}>Not assigned</span>
                        </div>
                      )}
                      <svg className="sidebar-select-chevron-new" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9"/></svg>
                    </button>
                  )}
                </div>
              </div>

              {/* Sentiment */}
              <div className="form-group-sidebar-new">
                <label className="sidebar-label-new">Sentiment</label>
                <div style={{ position: 'relative' }} ref={sentimentRef}>
                  <button type="button" className="sidebar-select-trigger-new has-icon-new cum-owner-trigger" onClick={() => setShowSentimentDropdown(p => !p)}>
                    {sentiment ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span className="ism-sentiment-dot" style={{ background: SENTIMENT_OPTIONS.find(s => s.value === sentiment)?.color }} />
                        <span>{SENTIMENT_OPTIONS.find(s => s.value === sentiment)?.label}</span>
                      </div>
                    ) : (
                      <span style={{ color: '#94a3b8' }}>Set value</span>
                    )}
                    <svg className="sidebar-select-chevron-new" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9"/></svg>
                  </button>
                  {showSentimentDropdown && (
                    <div className="cum-owner-dropdown">
                      <button type="button" className="cum-owner-option" onClick={() => { setSentiment(null); setShowSentimentDropdown(false); }}>
                        <span style={{ fontSize: 13, color: '#9ca3af', fontStyle: 'italic' }}>No value</span>
                      </button>
                      {SENTIMENT_OPTIONS.map(opt => (
                        <button key={opt.value} type="button" className="cum-owner-option" onClick={() => { setSentiment(opt.value); setShowSentimentDropdown(false); }}>
                          <span className="ism-sentiment-dot" style={{ background: opt.color }} />
                          <span style={{ fontSize: 13, color: '#111827' }}>{opt.label}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Links */}
              <div className="form-group-sidebar-new">
                <label className="sidebar-label-new">Links</label>
                {links.length === 0 ? (
                  <div className="ism-links-empty">No linked items yet</div>
                ) : (
                  <>
                    <div className="ism-link-card">
                      <span className="ism-link-icon" />
                      <span className="ism-link-label">{links[linkPage]?.label}</span>
                    </div>
                    {links.length > 1 && (
                      <div className="ism-link-dots">
                        {links.map((_, i) => (
                          <span key={i} className={`ism-link-dot ${i === linkPage ? 'active' : ''}`} onClick={() => setLinkPage(i)} />
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>

            </div>
          </div>

          {/* ── Footer ── */}
          <div className="task-modal-footer">
            <button className="cancel-btn" onClick={onClose} disabled={saving}>Cancel</button>
            <button className="submit-btn" onClick={handleSave} disabled={saving || !title.trim()}>
              {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Add insight'}
            </button>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxUrl && (
        <div className="cum-lightbox" onClick={() => setLightboxUrl(null)}>
          <img src={lightboxUrl} alt="attachment" className="cum-lightbox-img" onClick={e => e.stopPropagation()} />
          <button className="cum-lightbox-close" onClick={() => setLightboxUrl(null)}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
      )}
    </>
  );
};

export default InsightModal;
